import { buildComponent } from '#components'

import './video-section.css'

type VideoMediaEl = HTMLElement & {
  play: () => Promise<void> | void
  pause: () => Promise<void> | void
  hasAttribute: (name: string) => boolean
}

const selectors = {
  toggle: '[data-video-toggle]',
  label: '[data-video-toggle-label]',
  playIcon: '[data-video-toggle-icon-play]',
  pauseIcon: '[data-video-toggle-icon-pause]',
  videoMedia: 'video-media'
} as const

const VideoSection = buildComponent({
  name: 'video-section-component',
  onConnected: (component) => {
    const sectionRoot = component.closest('.shopify-section') as HTMLElement | null

    if (!sectionRoot) return

    const init = () => {
      const toggleButtons = Array.from(sectionRoot.querySelectorAll<HTMLButtonElement>(selectors.toggle))

      if (!toggleButtons.length) return

      const getParts = (btn: HTMLButtonElement) => ({
        labelEl: btn.querySelector<HTMLElement>(selectors.label),
        playIcon: btn.querySelector<HTMLElement>(selectors.playIcon),
        pauseIcon: btn.querySelector<HTMLElement>(selectors.pauseIcon)
      })

      const getActiveVideoMedia = (): VideoMediaEl | null => {
        const candidates = Array.from(sectionRoot.querySelectorAll<VideoMediaEl>(selectors.videoMedia))

        return candidates.find((el) => el.getClientRects().length > 0) || candidates[0] || null
      }

      const setUi = (isPlaying: boolean) => {
        toggleButtons.forEach((btn) => {
          const { labelEl, playIcon, pauseIcon } = getParts(btn)

          btn.classList.toggle('is-playing', isPlaying)
          btn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false')

          if (labelEl) {
            labelEl.textContent = isPlaying ? 'Pause video' : 'Play video'
          }
          if (playIcon) playIcon.hidden = isPlaying
          if (pauseIcon) pauseIcon.hidden = !isPlaying
        })
      }

      const syncFromVideoMedia = () => {
        const videoMedia = getActiveVideoMedia()

        if (!videoMedia) {
          setUi(false)

          return
        }

        setUi(videoMedia.hasAttribute('playing'))
      }

      let playingObserver: MutationObserver | null = null

      const attachVideoMediaObserver = () => {
        const videoMedia = getActiveVideoMedia()

        if (!videoMedia) return

        if (playingObserver) {
          playingObserver.disconnect()
        }

        playingObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.type === 'attributes' && m.attributeName === 'playing') {
              syncFromVideoMedia()
            }
          }
        })

        playingObserver.observe(videoMedia, {
          attributes: true,
          attributeFilter: ['playing']
        })

        syncFromVideoMedia()
      }

      attachVideoMediaObserver()

      toggleButtons.forEach((btn) => {
        btn.addEventListener('click', async () => {
          const videoMedia = getActiveVideoMedia()

          if (!videoMedia) return

          attachVideoMediaObserver()

          const isPlaying = videoMedia.hasAttribute('playing')

          try {
            if (isPlaying) {
              await videoMedia.pause()
            } else {
              await videoMedia.play()
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Video playback failed', error)
          }

          syncFromVideoMedia()
        })
      })

      window.addEventListener('resize', () => {
        attachVideoMediaObserver()
        syncFromVideoMedia()
      })
    }

    requestAnimationFrame(init)
  }
})

declare global {
  interface HTMLElementTagNameMap {
    'video-section-component': HTMLElement
  }
}

export default VideoSection
